import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, Loader2, User } from 'lucide-react';
import GoogleIcon from '../components/GoogleIcon';
import Logo from '../components/Logo';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signup, loginWithGoogle } = useAuth();




  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      await loginWithGoogle();
      navigate('/home');
    } catch (err) {
      setError('Google sign-up failed. Please try again.');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!termsAccepted) {
      return setError('You must agree to the Terms of Service to continue.');
    }
    if (password !== confirmPassword) {
      return setError('Passwords do not match.');
    }
    if (password.length < 6) {
      return setError('Password must be at least 6 characters long.');
    }
    
    setLoading(true);
    try {
      await signup(email, password, username); 
      navigate('/home');
    } catch (err) {
      setError(err.message || 'Failed to create an account.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4"><div className="scale-[2]"><Logo /></div></div>
          <h2 className="text-3xl font-bold text-white">Create an Account</h2>
          <p className="mt-2 text-gray-400">Join the community and help secure the ecosystem</p>
        </div>
        
        <div className="glass-card rounded-2xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">Username</label>
              <div className="relative">
                <input type="text" id="username" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full glass-card rounded-lg pl-10 pr-3 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50" placeholder="Choose a public username" required />
                <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              </div>
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">Email Address</label>
              <div className="relative">
                <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full glass-card rounded-lg pl-10 pr-3 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50" placeholder="you@example.com" required />
                <Mail size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              </div>
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">Password</label>
              <div className="relative">
                <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full glass-card rounded-lg pl-10 pr-3 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50" placeholder="Min. 6 characters" required />
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              </div>
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
              <div className="relative">
                <input type="password" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full glass-card rounded-lg pl-10 pr-3 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50" placeholder="Re-enter password" required />
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              </div>
            </div>
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="terms"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded bg-gray-700 border-gray-600 text-purple-500 focus:ring-purple-500 cursor-pointer flex-shrink-0"
              />
              <label htmlFor="terms" className="text-sm text-gray-300 cursor-pointer">
                I agree to the <span className="text-purple-400">Terms of Service</span> and <span className="text-purple-400">Privacy Policy</span>
              </label>
            </div>
            {error && (<div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3"><p className="text-red-400 text-sm">{error}</p></div>)}
            <button type="submit" disabled={loading} className="w-full glow-button glass-card neon-purple py-3 rounded-lg font-bold bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border border-purple-500/30 hover:from-purple-500/30 hover:to-pink-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {loading && <Loader2 className="animate-spin" size={20} />}
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-gray-500">or</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <button
              type="button"
              onClick={handleGoogle}
              disabled={loading}
              className="w-full glass-card py-3 rounded-lg font-bold text-white border border-white/10 hover:bg-white/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              <GoogleIcon />
              Continue with Google
            </button>
          </form>
        </div>
        
        <p className="text-center mt-6 text-gray-400">
          Already have an account? 
          <Link to="/login" className="text-purple-400 font-semibold hover:text-purple-300 ml-1">
            Log In
          </Link>
        </p>
      </div>
    </div>
  );
}
